/**
 * @file AssessmentCard — rating-scale answer card used in assessments
 * @version 0.2.1
 */

import React, { forwardRef, useId } from 'react';
import styles from './AssessmentCard.module.css';

export type AssessmentRank = 1 | 2 | 3 | 4 | 5;

/*
 * AssessmentCard — rating-scale answer card used in assessment question flows.
 *
 * Pairs a numeric rank (1–5) with a short title + multi-line description.
 * Designed to sit in a horizontal row of 5 as a multiple-choice rating UI.
 *
 * Three runtime states:
 *   - default: frosted dark-teal-grey surface (60% alpha + backdrop blur).
 *   - hover:   diagonal teal gradient + cyan border + outer cyan glow.
 *   - selected: bright-teal gradient + dark-blue ink (number, title, desc).
 *
 * Always renders as <button type="button"> — the card is intrinsically
 * interactive even when `onClick` is omitted (catalog cells), so keyboard
 * focus + :focus-visible behave consistently.
 *
 * Accessibility:
 *   - aria-labelledby points at the number + title nodes so the accessible
 *     name reads as "{number} {title}" (e.g., "1 No experience").
 *   - aria-describedby points at the description node — screen readers
 *     surface it as supplementary detail when paused on the button.
 *   - aria-pressed mirrors the `selected` prop for toggle semantics.
 */

export interface AssessmentCardProps {
  /** Numeric rank rendered as the large display digit. */
  number: AssessmentRank;
  /** Short title (e.g., "No experience"). */
  title: string;
  /**
   * Body description. CSS wraps naturally — pass a single string with no
   * hard line breaks; the card's chassis sizes the text box.
   */
  description: string;
  /** Picked state — bright-teal gradient + dark-blue ink when true. */
  selected?: boolean;
  /**
   * Number-only pill chip. Hides title + description, swaps the typography
   * to 24/Avenir centered, and adopts the pill chassis (height 48,
   * border-radius 42). `title` is still required and surfaces as the
   * accessible name; description is dropped (the host renders it in a
   * sibling panel). Used by AssessmentScale at sm tier.
   */
  compact?: boolean;
  /**
   * Card arrangement:
   *   - `stacked` (default): large display number above the title, full
   *     card chassis (the desktop row layout).
   *   - `inline`: smaller number sits beside the title on one row with the
   *     description below, in a shorter full-width chassis — the reference
   *     scale's stacked-list layout on narrow viewports.
   * Ignored when `compact` is set (the pill chip has its own chassis).
   */
  orientation?: 'stacked' | 'inline';
  /** Click handler. Card is always a <button>; omit for read-only cells. */
  onClick?: () => void;
  /**
   * Whether the card is a pick target. `true` (default) shows the hover
   * affordance (gradient + glow + pointer cursor). `false` is a read-only
   * reference card — no hover visual, default cursor — used when the scale is
   * shown for orientation rather than offered as a choice (e.g. AssessmentExample).
   */
  interactive?: boolean;
  /**
   * When set, the card is a radio option inside a radiogroup (AssessmentScale
   * drives selection via arrow keys). Renders `role="radio"` + `aria-checked`
   * instead of the default `aria-pressed` toggle-button semantics. Default
   * (unset) is unchanged — standalone / read-only cards stay toggle buttons.
   */
  radio?: boolean;
  /** Roving tabindex supplied by the parent radiogroup (radio mode). */
  tabIndex?: number;
  /** Keydown handler supplied by the parent radiogroup for arrow-key nav. */
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  /** Override the default accessible name composed from number + title. */
  'aria-label'?: string;
  /**
   * Colour treatment. `dark` (default) is the frosted-glass surface; `light`
   * is the light-surface variant (faint teal tint + navy border + navy ink;
   * hover deepens the tint; navy selected). A per-instance variant, not a
   * runtime theme mode.
   */
  theme?: 'light' | 'dark';
  /** Forwarded to the root element. */
  className?: string;
  /** Forces the hover visual on at-rest cards (catalog static-state preview). */
  'data-force-hover'?: boolean;
  /** Forces the focus-visible ring on at-rest cards (catalog static-state preview). */
  'data-force-focus'?: boolean;
}

export const AssessmentCard = forwardRef<HTMLButtonElement, AssessmentCardProps>(
  function AssessmentCard(
    {
      number,
      title,
      description,
      selected = false,
      compact = false,
      orientation = 'stacked',
      onClick,
      interactive = true,
      radio = false,
      theme = 'dark',
      className,
      'data-force-hover': dataForceHover,
      'data-force-focus': dataForceFocus,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) {
    const numId = useId();
    const titleId = useId();
    const descId = useId();

    const inline = !compact && orientation === 'inline';

    const classes = [
      styles.card,
      theme === 'light' ? styles.light : '',
      selected ? styles.selected : '',
      compact ? styles.compact : '',
      inline ? styles.inline : '',
      !interactive ? styles.static : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    const accessibleNameProps = ariaLabel
      ? { 'aria-label': ariaLabel }
      : { 'aria-labelledby': `${numId} ${titleId}` };

    const numberNode = (
      <span id={numId} className={styles.number}>
        {number}
      </span>
    );
    const titleNode = (
      <span id={titleId} className={styles.title}>
        {title}
      </span>
    );
    const descNode = (
      <span id={descId} className={styles.description}>
        {description}
      </span>
    );

    return (
      <button
        ref={ref}
        type="button"
        className={classes}
        onClick={onClick}
        role={radio ? 'radio' : undefined}
        aria-checked={radio ? selected : undefined}
        aria-pressed={radio ? undefined : selected}
        aria-describedby={compact ? undefined : descId}
        data-force-hover={dataForceHover ? '' : undefined}
        data-force-focus={dataForceFocus ? '' : undefined}
        {...accessibleNameProps}
        {...rest}
      >
        {compact && (
          <>
            {numberNode}
            <span id={titleId} className={styles.visuallyHidden}>
              {title}
            </span>
          </>
        )}
        {inline && (
          <>
            <span className={styles.head}>
              {numberNode}
              {titleNode}
            </span>
            {descNode}
          </>
        )}
        {!compact && !inline && (
          <>
            {numberNode}
            {titleNode}
            {descNode}
          </>
        )}
      </button>
    );
  },
);
